import React, { useRef, useState } from 'react';
import { Card, LoadingSpinner } from 'components/common';
import PropTypes from 'prop-types';
import ReactECharts from 'echarts-for-react';
import { FiUsers } from 'react-icons/fi';
import { CHART_PALETTE, CHART_FONT, CHART_TOOLTIP } from 'helpers/echartsTheme';

const COLORS = { from_lead: CHART_PALETTE[3], direct: CHART_PALETTE[1] };
const FALLBACK_COLOR = '#94A3B8';

const fmtM = (n) => {
  const v = Number(n || 0);
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000)     return `$${(v / 1_000).toFixed(0)}K`;
  return `$${v.toFixed(0)}`;
};

const buildOption = (items, total, clickable) => {
  const centerText = [
    {
      type: 'text',
      left: 'center',
      top: '40%',
      style: { text: String(total), fontSize: 20, fontWeight: 800, fill: '#111827', fontFamily: CHART_FONT.fontFamily }
    },
    {
      type: 'text',
      left: 'center',
      top: '55%',
      style: { text: 'PROJECTS', fontSize: 9, fill: '#9CA3AF', fontFamily: CHART_FONT.fontFamily }
    }
  ];

  if (total === 0) {
    return {
      textStyle: { fontFamily: CHART_FONT.fontFamily },
      graphic: centerText,
      series: [{
        type: 'pie',
        radius: ['50%', '80%'],
        silent: true,
        label: { show: false },
        labelLine: { show: false },
        data: [{ value: 1, itemStyle: { color: '#F3F4F6' } }]
      }]
    };
  }

  return {
    textStyle: { fontFamily: CHART_FONT.fontFamily },
    tooltip: {
      ...CHART_TOOLTIP,
      trigger: 'item',
      formatter: (params) => {
        const item = items[params.dataIndex];
        if (!item) return '';
        const pct = total > 0 ? ((item.count / total) * 100).toFixed(1) : 0;
        return [
          `<div style="font-weight:700;color:#111827">${item.label}</div>`,
          `<div style="font-size:12px;color:#374151">${item.count} projects (${pct}%)</div>`,
          `<div style="font-size:11px;color:#6b7280">${fmtM(item.value)}</div>`
        ].join('');
      }
    },
    graphic: centerText,
    series: [{
      type: 'pie',
      radius: ['50%', '80%'],
      cursor: clickable ? 'pointer' : 'default',
      label: { show: false },
      labelLine: { show: false },
      itemStyle: { opacity: 0.85, borderColor: '#ffffff', borderWidth: 2 },
      emphasis: {
        scale: true,
        scaleSize: 4,
        itemStyle: {
          opacity: 1,
          borderWidth: 3,
          shadowBlur: 6,
          shadowOffsetY: 3,
          shadowColor: 'rgba(0,0,0,0.15)'
        }
      },
      data: items.map((item) => ({
        value: item.count,
        name: item.label,
        itemStyle: { color: COLORS[item.source] || FALLBACK_COLOR }
      }))
    }]
  };
};

const LeadSourceChart = ({ data, loading, error, onDrillDown }) => {
  const [hover, setHover] = useState(null);
  const chartRef = useRef(null);

  if (loading) return (
    <Card className="tw-p-6 tw-border tw-border-gray-200">
      <div className="tw-flex tw-justify-center tw-py-12"><LoadingSpinner size="lg" /></div>
    </Card>
  );
  if (error) return (
    <Card className="tw-p-4 tw-bg-red-50 tw-border tw-border-red-200">
      <p className="tw-text-red-600 tw-text-sm">Error loading lead source data</p>
    </Card>
  );

  const items = data || [];
  const total = items.reduce((s, d) => s + d.count, 0);

  const handleClick = (item) => {
    if (!onDrillDown) return;
    const fromLead = item.source === 'from_lead' ? 'true' : 'false';
    onDrillDown(item.label, { from_lead: fromLead });
  };

  const dispatch = (action) => {
    const instance = chartRef.current?.getEchartsInstance?.();
    if (instance) instance.dispatchAction(action);
  };

  const cardEnter = (i) => {
    setHover(i);
    if (total > 0) dispatch({ type: 'highlight', seriesIndex: 0, dataIndex: i });
  };
  const cardLeave = (i) => {
    setHover(null);
    if (total > 0) dispatch({ type: 'downplay', seriesIndex: 0, dataIndex: i });
  };

  const onEvents = {
    click:     (params) => { const item = items[params.dataIndex]; if (item) handleClick(item); },
    mouseover: (params) => setHover(params.dataIndex),
    mouseout:  () => setHover(null)
  };

  return (
    <Card className="tw-border tw-border-gray-200 tw-bg-gradient-to-br tw-from-violet-50 tw-via-white tw-to-emerald-50 tw-shadow-sm">
      <div className="tw-p-5 tw-border-b tw-border-gray-200">
        <h3 className="tw-font-bold tw-text-gray-900 tw-flex tw-items-center tw-gap-2">
          <FiUsers className="tw-text-violet-600" size={18} />
          Lead vs Direct
        </h3>
        <p className="tw-text-xs tw-text-gray-500 tw-mt-0.5">
          Project source breakdown
          {onDrillDown && <span className="tw-ml-1 tw-text-blue-500">· Click to see details</span>}
        </p>
      </div>

      <div className="tw-p-5 tw-flex tw-flex-col sm:tw-flex-row tw-items-center tw-gap-5">
        {/* Donut */}
        <div className="tw-flex-shrink-0">
          <ReactECharts
            ref={chartRef}
            option={buildOption(items, total, !!onDrillDown)}
            style={{ height: 180, width: 180 }}
            onEvents={total > 0 ? onEvents : undefined}
            notMerge
          />
        </div>

        {/* Legend */}
        <div className="tw-flex-1 tw-space-y-3 tw-w-full">
          {items.map((item, i) => {
            const color = COLORS[item.source] || FALLBACK_COLOR;
            const pct = total > 0 ? ((item.count / total) * 100).toFixed(1) : 0;
            return (
              <div
                key={item.source}
                className="tw-rounded-lg tw-p-3.5 tw-border tw-transition-all"
                style={{
                  borderColor: hover === i ? color : 'transparent',
                  backgroundColor: hover === i ? `${color}12` : '#F9FAFB',
                  cursor: onDrillDown ? 'pointer' : 'default'
                }}
                onMouseEnter={() => cardEnter(i)} onMouseLeave={() => cardLeave(i)}
                onClick={() => handleClick(item)}
              >
                <div className="tw-flex tw-items-center tw-justify-between">
                  <div className="tw-flex tw-items-center tw-gap-2">
                    <span className="tw-w-3 tw-h-3 tw-rounded-xl" style={{ backgroundColor: color }} />
                    <span className="tw-text-sm tw-font-semibold tw-text-gray-700">{item.label}</span>
                  </div>
                  <span className="tw-text-xl tw-font-bold" style={{ color }}>{item.count}</span>
                </div>
                <div className="tw-flex tw-items-center tw-justify-between tw-mt-1">
                  <span className="tw-text-xs tw-font-semibold tw-px-2 tw-py-0.5 tw-rounded-xl" style={{ backgroundColor: `${color}18`, color }}>
                    {pct}%
                  </span>
                  <span className="tw-text-xs tw-font-semibold tw-text-gray-500">{fmtM(item.value)}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Card>
  );
};

LeadSourceChart.propTypes = {
  data:        PropTypes.array,
  loading:     PropTypes.bool,
  error:       PropTypes.any,
  onDrillDown: PropTypes.func
};
LeadSourceChart.defaultProps = { data: [], loading: false, error: null, onDrillDown: null };

export default LeadSourceChart;
